import { ArrowDropDown, ArrowDropUp, Close } from '@mui/icons-material';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DateCalendar, LocalizationProvider, MultiSectionDigitalClock } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format, startOfDay } from 'date-fns';
import React, { FC, ReactNode, useMemo, useState } from 'react';

import { timezones } from '../../../../assets/data/timezones';
import { ITimezone } from '../../../../types/timezone';
import {
  detectUserTimezone,
  formatZonedDateTime,
  getTimezoneDisplayText,
  getWallClock,
  isSameWallClockMinute,
  timeOfDay,
  TIME_REFERENCE_DATE,
  wallClockDay,
  wallClockOf,
} from '../../../../utils/date';
import {
  dayToUtcMidnightIso,
  earliestHoldDay,
  getReleaseMinTime,
  holdDayInDays,
  isBeforeReleaseMin,
  isBeyondMaxDay,
  nowInTimezone,
  releaseDateInDays,
  resolveWallClockIso,
  utcIsoToWallClock,
  utcMidnightIsoToDay,
} from '../../common/releaseDate';
import DropdownPopper from '../DropdownPopper';
import { RECIPIENTS_COPY } from '../recipientsCopy';

const { timing } = RECIPIENTS_COPY.settings;

const TIME_STEP_MINUTES = 5;
const COMMIT_GRACE_MS = TIME_STEP_MINUTES * 60 * 1000;

const CALENDAR_MIN_DATE = new Date(1900, 0, 1);
const CALENDAR_MAX_DATE = new Date(2099, 11, 31, 23, 59, 59);
const POPOVER_HEIGHT = 466;
const POPOVER_WIDTH = 433;
const DATE_ONLY_POPOVER_HEIGHT = 384;
const DATE_ONLY_POPOVER_WIDTH = 342;
// Popover's default marginThreshold on each side.
const VIEWPORT_MARGIN = 16;
// Below this width the calendar and clock no longer fit side by side, so the clock moves under the calendar.
const STACKED_LAYOUT_QUERY = `(max-width:${POPOVER_WIDTH + VIEWPORT_MARGIN * 2 - 1}px)`;
const CLOCK_HEIGHT = 336;
const STACKED_CLOCK_HEIGHT = 176;
// Header, calendar, clock and footer stacked; used to choose whether the popover opens up or down.
const STACKED_POPOVER_HEIGHT = 620;
const VIEWPORT_MAX_HEIGHT = `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`;
const VIEWPORT_MAX_WIDTH = `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`;

type TDateSelectionState = 'partial' | 'shallow' | 'finish';

interface ITriggerProps {
  displayValue: string;
  isOpen: boolean;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
}

const DATE_ONLY_FORMAT = 'dd MMM yyyy';

const DEFAULT_DATA_ACTION_PREFIX = 'create_send_release';

interface IProps {
  value: string | null;
  onChange?: (isoOrEmpty: string) => void;
  // Edit flows: only a real change is reported, on OK (or on the pick itself in date-only mode);
  // dismissing the popover discards the draft.
  onConfirm?: (isoOrEmpty: string, timezone: ITimezone) => void;
  label: string;
  placeholder: string;
  helperText?: string;
  error?: boolean;
  maxSelectableDate?: Date | null;
  dateOnly?: boolean;
  minLeadMs?: number;
  timezone?: ITimezone | null;
  onTimezoneChange?: (timezone: ITimezone) => void;
  clearable?: boolean;
  disabled?: boolean;
  dataActionPrefix?: string;
  // Replaces the outlined field with a custom trigger; the popover anchors to the element that calls onOpen.
  renderTrigger?: (props: ITriggerProps) => ReactNode;
}

const ReleaseDatePicker: FC<IProps> = ({
  value,
  onChange,
  onConfirm,
  label,
  placeholder,
  helperText,
  error = false,
  maxSelectableDate,
  dateOnly,
  minLeadMs = 0,
  timezone: selectedTimezone,
  onTimezoneChange,
  clearable = true,
  disabled = false,
  dataActionPrefix = DEFAULT_DATA_ACTION_PREFIX,
  renderTrigger,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openUp, setOpenUp] = useState(false);
  const [ownTimezone, setOwnTimezone] = useState<ITimezone>(detectUserTimezone);
  const [draftIso, setDraftIso] = useState<string | null>(null);
  const [draftTimezone, setDraftTimezone] = useState<ITimezone | null>(null);
  // The calendar and clock show a rejected pick while the draft keeps the last valid one, so OK must not save it.
  const [isPickRejected, setIsPickRejected] = useState(false);
  const isDeferred = Boolean(onConfirm);
  const committedTimezone = selectedTimezone ?? ownTimezone;
  const timezone = draftTimezone ?? committedTimezone;
  const currentIso = draftIso ?? value;
  const [day, setDay] = useState<Date | null>(null);
  const [time, setTime] = useState<Date | null>(null);
  const [hovered, setHovered] = useState(false);
  const [tzAnchorEl, setTzAnchorEl] = useState<HTMLElement | null>(null);
  const [tzQuery, setTzQuery] = useState('');
  const isNarrowViewport = useMediaQuery(STACKED_LAYOUT_QUERY, { noSsr: true });

  const open = Boolean(anchorEl);
  const tzOpen = Boolean(tzAnchorEl);
  const earliestInstant = () => new Date(Date.now() + minLeadMs);
  const commitLeadMs = minLeadMs > 0 ? minLeadMs : -COMMIT_GRACE_MS;
  const minTime = getReleaseMinTime(day, timezone, TIME_STEP_MINUTES, earliestInstant());

  const isStacked = !dateOnly && isNarrowViewport;
  const clockHeight = isStacked ? STACKED_CLOCK_HEIGHT : CLOCK_HEIGHT;
  const popoverHeight = useMemo(() => {
    if (dateOnly) {
      return DATE_ONLY_POPOVER_HEIGHT;
    }

    return isStacked ? STACKED_POPOVER_HEIGHT : POPOVER_HEIGHT;
  }, [dateOnly, isStacked]);
  const popoverWidth = dateOnly ? DATE_ONLY_POPOVER_WIDTH : POPOVER_WIDTH;
  const quickPicks = dateOnly ? timing.holdQuickPicks : timing.quickPicks;
  const minDate = dateOnly
    ? earliestHoldDay()
    : minLeadMs > 0
    ? wallClockDay(nowInTimezone(timezone, earliestInstant()))
    : CALENDAR_MIN_DATE;

  // The draft, not the stored value, decides which offset the popover labels show in the edit flows.
  const labelInstant = useMemo(() => (currentIso ? new Date(currentIso) : new Date()), [currentIso]);

  const timezoneOptions = useMemo(
    () => timezones.map((tz) => ({ tz, label: getTimezoneDisplayText(tz, labelInstant) })),
    [labelInstant],
  );

  // The stored label is searched too, so a zone stays findable by its standard offset while on DST.
  const filteredTimezones = useMemo(() => {
    const query = tzQuery.trim().toLowerCase();
    return query
      ? timezoneOptions.filter(
          ({ tz, label: tzLabel }) => tz.text.toLowerCase().includes(query) || tzLabel.toLowerCase().includes(query),
        )
      : timezoneOptions;
  }, [tzQuery, timezoneOptions]);

  const timezoneLabel = useMemo(() => getTimezoneDisplayText(timezone, labelInstant), [timezone, labelInstant]);

  const displayValue = useMemo(() => {
    if (!value) {
      return '';
    }
    if (dateOnly) {
      return format(utcMidnightIsoToDay(value), DATE_ONLY_FORMAT);
    }

    return formatZonedDateTime(value, committedTimezone);
  }, [value, committedTimezone, dateOnly]);

  // Returns the accepted ISO, or null when the pick is rejected.
  const commit = (nextDay: Date, nextTime: Date | null, nextTz: ITimezone): string | null => {
    setDay(nextDay);
    setTime(nextTime);
    if (isBeyondMaxDay(nextDay, maxSelectableDate)) {
      setIsPickRejected(true);
      return null;
    }
    const wallClock = wallClockOf(nextDay, nextTime ?? timeOfDay(0));
    // Back on the stored minute, keep the stored instant (and its seconds) rather than the draft's.
    const isStoredMinute = Boolean(value) && isSameWallClockMinute(wallClock, getWallClock(value as string, nextTz));
    const iso = dateOnly
      ? dayToUtcMidnightIso(nextDay)
      : resolveWallClockIso(wallClock, nextTz, isStoredMinute ? value : currentIso);
    if (isBeforeReleaseMin(iso, commitLeadMs)) {
      setIsPickRejected(true);
      return null;
    }
    setIsPickRejected(false);
    if (isDeferred) {
      setDraftIso(iso);
    } else {
      onChange?.(iso);
    }
    return iso;
  };

  const showStoredIn = (iso: string, tz: ITimezone) => {
    const stored = utcIsoToWallClock(iso, tz);
    setDay(wallClockDay(stored));
    setTime(timeOfDay(stored.hours, stored.minutes));
  };

  const openPickerAt = (element: HTMLElement) => {
    // Reseeding the calendar while open would hide the draft OK saves.
    if (disabled || open) {
      return;
    }
    const rect = element.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const willOpenUp = spaceBelow < popoverHeight && spaceAbove > spaceBelow;
    setOpenUp(willOpenUp);
    if (!value) {
      setDay(null);
      setTime(null);
    } else if (dateOnly) {
      setDay(utcMidnightIsoToDay(value));
      setTime(null);
    } else {
      showStoredIn(value, timezone);
    }
    setAnchorEl(element);
  };

  const openPicker = (event: React.MouseEvent<HTMLElement>) => openPickerAt(event.currentTarget);
  const close = () => {
    setAnchorEl(null);
    setTzAnchorEl(null);
    setTzQuery('');
    setDraftIso(null);
    setDraftTimezone(null);
    setIsPickRejected(false);
  };
  // A pick that closes the popover itself passes its ISO, since the draft state is not updated yet.
  const confirmIso = (nextIso: string | null) => {
    // Re-picking the stored day and time is not a change, so OK reports nothing and nothing is saved.
    const isInstantChanged = nextIso !== null && (!value || Date.parse(nextIso) !== Date.parse(value));
    const isTimezoneChanged = timezone.text !== committedTimezone.text;
    if (onConfirm && (isInstantChanged || isTimezoneChanged)) {
      const confirmedIso = nextIso ?? value ?? '';
      // The minimum lead moves with the clock, so a pick that was valid when made can be too soon by OK.
      if (confirmedIso && isBeforeReleaseMin(confirmedIso, commitLeadMs)) {
        setIsPickRejected(true);
        return;
      }
      onConfirm(confirmedIso, timezone);
    }
    close();
  };
  const confirm = () => confirmIso(draftIso);
  const closeTimezone = () => {
    setTzAnchorEl(null);
    setTzQuery('');
  };

  const handleDateChange = (next: Date | null, selectionState?: TDateSelectionState) => {
    if (!next) {
      return;
    }
    const iso = commit(startOfDay(next), dateOnly ? null : time ?? timeOfDay(0), timezone);

    if (dateOnly && selectionState === 'finish') {
      if (isDeferred && iso) {
        confirmIso(iso);
      } else {
        close();
      }
    }
  };
  const handleTimeChange = (next: Date | null) => {
    if (next) {
      commit(day ?? wallClockDay(nowInTimezone(timezone)), timeOfDay(next.getHours(), next.getMinutes()), timezone);
    }
  };
  const selectTimezone = (tz: ITimezone) => {
    if (isDeferred) {
      setDraftTimezone(tz);
    } else {
      setOwnTimezone(tz);
      onTimezoneChange?.(tz);
    }
    closeTimezone();
    // A rejected wall clock keeps the stored instant, shown in the new zone, so zone and ISO stay in step.
    if (day && !commit(day, time, tz) && currentIso) {
      showStoredIn(currentIso, tz);
      setIsPickRejected(false);
    }
  };
  const handleQuickPick = (days: number) => {
    if (dateOnly) {
      commit(holdDayInDays(days), null, timezone);
      return;
    }
    const pick = releaseDateInDays(days);
    commit(startOfDay(pick), timeOfDay(pick.getHours(), pick.getMinutes()), timezone);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setDay(null);
    setTime(null);
    if (onConfirm) {
      onConfirm('', committedTimezone);
    } else {
      onChange?.('');
    }
    close();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {renderTrigger ? (
        renderTrigger({ displayValue, isOpen: open, onOpen: openPicker })
      ) : (
        <TextField
          fullWidth
          focused={open}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          disabled={disabled}
          value={displayValue}
          onClick={openPicker}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          data-action={`${dataActionPrefix}_date_open`}
          sx={{
            '& .MuiFormHelperText-root': { position: 'static', mt: 0.375, mx: 1.75, mb: 0 },
          }}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              readOnly: true,
              sx: { cursor: 'pointer' },
              endAdornment: (
                <InputAdornment position="end">
                  {Boolean(value) && hovered && clearable && !disabled && (
                    <IconButton aria-label={`Clear ${label.toLowerCase()}`} onClick={handleClear} sx={{ mr: -0.5 }}>
                      <Close sx={{ fontSize: 20 }} />
                    </IconButton>
                  )}
                  <IconButton edge="end" aria-label="Choose date">
                    <InsertInvitationIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
            htmlInput: { 'aria-label': label, style: { cursor: 'pointer' } },
          }}
        />
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: openUp ? 'top' : 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: openUp ? 'bottom' : 'top', horizontal: 'left' }}
        disableEnforceFocus
        slotProps={{
          paper: {
            sx: {
              mt: openUp ? -1 : 1,
              borderRadius: 2,
              overflow: 'hidden',
              width: popoverWidth,
              height: isStacked ? 'auto' : popoverHeight,
              ...(isStacked ? { maxHeight: VIEWPORT_MAX_HEIGHT, maxWidth: VIEWPORT_MAX_WIDTH, overflowY: 'auto' } : {}),
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {!dateOnly && (
          <Box sx={{ py: 1.5, px: 2, borderBottom: '1px solid', borderBottomColor: 'action.selected', flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 500, fontSize: '12px', lineHeight: 1, color: 'text.secondary', mb: 0.75 }}>
              {timing.timezone}
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                setTzAnchorEl(tzOpen ? null : event.currentTarget)
              }
              data-action={`${dataActionPrefix}_timezone`}
              sx={{
                width: '100%',
                textAlign: 'left',
                border: '1px solid',
                borderColor: 'grey.400',
                borderRadius: 1,
                py: 1,
                px: 1.5,
                backgroundColor: 'background.paper',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: 1.4,
                color: 'text.primary',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {timezoneLabel}
              </Box>
              {tzOpen ? (
                <ArrowDropUp sx={{ fontSize: 20, color: 'action.active' }} />
              ) : (
                <ArrowDropDown sx={{ fontSize: 20, color: 'action.active' }} />
              )}
            </Box>

            <DropdownPopper
              open={tzOpen}
              anchorEl={tzAnchorEl}
              onClose={closeTimezone}
              matchAnchorWidth
              paperSx={{ maxHeight: 220, py: 0, overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
              <Box
                component="input"
                autoFocus
                placeholder="Search timezone…"
                value={tzQuery}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTzQuery(event.target.value)}
                sx={{
                  border: 'none',
                  borderBottom: '1px solid',
                  borderBottomColor: 'action.selected',
                  py: 1,
                  px: 1.5,
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: 1.4,
                  outline: 'none',
                  flexShrink: 0,
                }}
              />
              <Box sx={{ overflowY: 'auto' }}>
                {filteredTimezones.map(({ tz, label: tzLabel }) => (
                  <Box
                    component="button"
                    type="button"
                    key={tz.text}
                    onClick={() => selectTimezone(tz)}
                    sx={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: 0,
                      backgroundColor: (theme: Theme) =>
                        tz.text === timezone.text ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      py: 1,
                      px: 1.5,
                      fontWeight: 400,
                      fontSize: '13px',
                      lineHeight: 1.4,
                      color: 'text.primary',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: (theme: Theme) =>
                          tz.text === timezone.text ? alpha(theme.palette.primary.main, 0.08) : 'action.hover',
                      },
                    }}
                  >
                    {tzLabel}
                  </Box>
                ))}
              </Box>
            </DropdownPopper>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flex: '1 1 0%',
            minHeight: 0,
            overflow: 'hidden',
            ...(dateOnly ? { justifyContent: 'center' } : {}),
            ...(isStacked ? { flex: '0 0 auto', flexDirection: 'column', alignItems: 'center' } : {}),
          }}
        >
          <DateCalendar
            value={day}
            onChange={handleDateChange}
            views={['year', 'day']}
            minDate={minDate}
            maxDate={CALENDAR_MAX_DATE}
            shouldDisableDate={(candidate: Date) => isBeyondMaxDay(candidate, maxSelectableDate)}
            disablePast
            sx={{
              flexShrink: 0,

              '& .MuiPickersYear-root': {
                flexBasis: '25%',
              },

              '& .MuiPickersYear-yearButton.Mui-disabled': {
                pointerEvents: 'none',
                color: 'text.secondary',
              },
            }}
          />
          {!dateOnly && (
            <>
              <Divider orientation={isStacked ? 'horizontal' : 'vertical'} flexItem />
              <MultiSectionDigitalClock
                value={time}
                referenceDate={TIME_REFERENCE_DATE}
                onChange={handleTimeChange}
                ampm={false}
                timeSteps={{ hours: 1, minutes: TIME_STEP_MINUTES }}
                minTime={minTime}
                sx={{
                  flex: isStacked ? '0 0 auto' : '1 1 0%',
                  minWidth: 0,
                  ...(isStacked ? { width: '100%' } : {}),
                  height: clockHeight,
                  maxHeight: clockHeight,
                  justifyContent: 'center',
                  '& .MuiMultiSectionDigitalClockSection-root': { height: clockHeight, maxHeight: clockHeight },
                  '& .MuiMultiSectionDigitalClockSection-item': {
                    fontWeight: 400,
                    fontSize: '1.14286rem',
                    lineHeight: 1.5,
                    width: 48,
                    minWidth: 48,
                    height: 40,
                    my: 0.25,
                    mx: 0.5,
                    padding: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 0,
                    '&.Mui-selected': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
                    '&.Mui-selected:hover': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
                    '&.Mui-disabled': { opacity: 0.38, pointerEvents: 'none' },
                  },
                }}
              />
            </>
          )}
        </Box>

        <Divider />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            py: 1,
            px: 1.5,
            flexShrink: 0,
            ...(isStacked ? { flexWrap: 'wrap' } : {}),
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            {quickPicks.map((pick) => (
              <Chip
                key={pick.days}
                label={pick.label}
                variant="outlined"
                color="primary"
                clickable
                onClick={() => handleQuickPick(pick.days)}
                data-action={`${dataActionPrefix}_date_in_${pick.days}`}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Box>
          <Button
            variant="text"
            onClick={confirm}
            disabled={isDeferred && isPickRejected}
            data-action={`${dataActionPrefix}_date_ok`}
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              ...(dateOnly ? { minWidth: 'auto', px: 1 } : {}),
              ...(isStacked ? { ml: 'auto' } : {}),
            }}
          >
            {timing.ok}
          </Button>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default ReleaseDatePicker;
